import { useEffect, useState } from "react";

import { Node } from "@tiptap/pm/model";
import { EditorState } from "@tiptap/pm/state";

import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import { LANG } from "@/constants/Language";
import useHasFeature from "@/features/useHasFeature";
import useUser from "@/users/useUser";
import { getDynamicData } from "@/view/TiptapArticleBody/extensions/DataProvider";
import { TRANSLATION_DEFAULT_ERROR_MESSAGE } from "./TranslateDrawer.constants";
import {
  ILanguage,
  ITranslationData,
  ITranslationError,
  TLanguageChangeHandler,
} from "./TranslateDrawer.interface";
import { baseStyles, baseWrapperStyles } from "./TranslateDrawer.style";
import TranslateDrawerBody from "./TranslateDrawerBody";
import TranslateDrawerFooter from "./TranslateDrawerFooter";
import TranslateDrawerHeader from "./TranslateDrawerHeader";
import {
  DEFAULT_LANGUAGE,
  getLanguagePreference,
  updateLanguagePreference,
} from "./utils/languagePreferences";
import useTranslateText from "./utils/useTranslateText";

interface TranslateDrawerProps {
  state: EditorState;
  identifier: string;
  text: string;
  onClose: () => void;
  supportedLanguages: ILanguage[];
  node: Node;
}

const TranslateDrawer = ({
  state,
  identifier,
  text,
  onClose,
  supportedLanguages,
  node,
}: TranslateDrawerProps) => {
  const { data: user } = useUser();
  const { levelId, userProperties } = getDynamicData(state);
  const { isTeacher } = userProperties;

  const [translationData, setTranslationData] = useState<
    ITranslationData | undefined
  >(undefined);
  const [translationError, setTranslationError] = useState<
    ITranslationError | undefined
  >(undefined);
  const [language, setLanguage] = useState<ILanguage | undefined>(undefined);
  const { translateText, isPending: isLoading, isError } = useTranslateText();
  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);

  useEffect(() => {
    const fetchPreferredLanguage = async () => {
      const preferredLanguage = await getLanguagePreference(user);

      handleSetLanguage(preferredLanguage);
    };

    fetchPreferredLanguage();
  }, [user]);

  const handleSetLanguage: TLanguageChangeHandler = async (lang) => {
    setTranslationError(undefined);
    setLanguage(lang || undefined);
    setTranslationData(undefined);
    updateLanguagePreference(user, lang);

    if (lang && lang?.code !== DEFAULT_LANGUAGE?.code) {
      try {
        const data = await translateText({ text, language: lang });
        // Transform the data to match the expected ITranslationData interface format
        const transformedData: ITranslationData = {
          engine: data.engine, // Keep compatibility with the interface
          identifier: data.identifier,
          source_language: data.source_language as LANG,
          target_language: {
            code: data.target_language,
            name:
              supportedLanguages.find((l) => l.code === data.target_language)
                ?.name || data.target_language,
          },
          timestamp: data.timestamp,
          translated_text: data.translated_text,
        };

        setTranslationData(transformedData);
      } catch (err) {
        console.error("Translation failed", err);
        setTranslationError({
          errorMessage: TRANSLATION_DEFAULT_ERROR_MESSAGE,
          error_loading_translation: true,
        });
      }
    }
  };

  useEffect(() => {
    // fetch translated text and open drawer when a user
    // already has a language preference
    handleSetLanguage(language);
  }, []);

  const { identifier: translationIdentifier, translated_text: translatedText } =
    (translationData as ITranslationData) || {};

  const showDrawerContent = !!language?.code;

  return (
    <div
      id={`translate-drawer-${identifier}`}
      css={[baseStyles(showLunaAiBranding)]}
    >
      <div css={baseWrapperStyles}>
        <TranslateDrawerHeader
          hasBody={isLoading || !!translatedText}
          languages={supportedLanguages}
          onLanguageChange={handleSetLanguage}
          onCloseButtonClick={onClose}
          selectedLanguage={language}
          state={state}
          node={node}
          {...(translationData ? { translationData } : {})}
        />
        {showDrawerContent && (
          <>
            <TranslateDrawerBody
              translationError={translationError || undefined}
              isError={isError}
              isLoading={isLoading}
              language={language}
              translationData={translationData || undefined}
            />

            <TranslateDrawerFooter
              identifier={identifier}
              contentId={levelId}
              isTeacher={isTeacher}
              extraData={{
                ...(translationError
                  ? { error_loading_translation: isError }
                  : {}),
                target_language: language,
                text,
                ...(translationData
                  ? { translation_identifier: translationIdentifier }
                  : {}),
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TranslateDrawer;
